/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Factory, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DotGrid from "./DotGrid";
import { useGsapScrollParallax } from "@/hooks/useGsapScrollParallax";

type SpecProfile = "airCooler" | "heatExchanger" | "water" | "default";

interface ProjectData {
	id: string;
	title: string;
	/** Key under `worldwideSlider.projects.*` for the localized application text. */
	appKey?: string;
	capacity?: string;
	image: string;
	/** Key under `worldwideSlider.countries.*`. */
	country?: string;
	/** Drives the localized spec table (see buildSpecs). */
	profile?: SpecProfile;
	/** Keys under `worldwideSlider.tags.*`. */
	tagKeys?: string[];
}

// Title/capacity/image stay literal (proper nouns, units, assets). All display
// text resolves from the active locale via the keys below.
const projectsData = {
	projects: [
		{
			id: "18",
			title: "EIL - ONGC, Panipat",
			appKey: "eilOngc",
			capacity: "2800 kW",
			image: "/images/worldwide/ONGC.jpg",
			country: "india",
			profile: "heatExchanger" as SpecProfile,
			tagKeys: ["oilGasProcessing", "thermalSystems"],
		},
		{
			id: "13",
			title: "ADNOC - BAB Far North",
			appKey: "adnocBab",
			capacity: "",
			image: "/images/worldwide/ADNO.jpeg",
			country: "uae",
			profile: "airCooler" as SpecProfile,
			tagKeys: ["oilGasProcessing", "thermalSystems"],
		},
		{
			id: "5",
			title: "NTPC-Khargone",
			appKey: "ntpcKhargone",
			capacity: "300 kW & 2400 kW",
			image: "/images/worldwide/DoosanScodaPowerIndiaG.jpg",
			country: "india",
			profile: "airCooler" as SpecProfile,
			tagKeys: ["powerGeneration", "thermalSystems"],
		},
		{
			id: "19",
			title: "ABB Limited",
			appKey: "abb",
			capacity: "200 kW",
			image: "/images/worldwide/abplimited.png",
			country: "india",
			profile: "airCooler" as SpecProfile,
			tagKeys: ["powerGeneration", "equipmentCooling"],
		},
		{
			id: "6",
			title: "Bangalore V Valley Project",
			appKey: "bangaloreVValley",
			capacity: "872.2 kW",
			image: "/images/worldwide/Banglore.jpeg",
			country: "india",
			profile: "water" as SpecProfile,
			tagKeys: ["waterTreatment", "wastewaterProcessing"],
		},
	],
};

const projects: ProjectData[] = projectsData.projects;

const WorldWideProjects: React.FC = () => {
	const { t } = useTranslation();
	const sectionRef = useRef<HTMLElement | null>(null);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isAnimating, setIsAnimating] = useState(false);
	const [isAutoPlaying, setIsAutoPlaying] = useState(true);
	const [isExpanded, setIsExpanded] = useState(false);
	const [selectedProject, setSelectedProject] = useState<ProjectData | null>(
		null,
	);
	const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
	const touchStartX = useRef(0);
	const touchEndX = useRef(0);
	const [direction, setDirection] = useState(0);
	const [showSideCards, setShowSideCards] = useState(false);
	useGsapScrollParallax(sectionRef, { y: -100, opacity: 0.9, scale: 0.98 });

	useEffect(() => {
		if (typeof window === "undefined") return;
		const mq = window.matchMedia("(min-width: 1024px)");
		const update = () => setShowSideCards(mq.matches);
		update();
		mq.addEventListener("change", update);
		return () => mq.removeEventListener("change", update);
	}, []);

	const AUTO_PLAY_INTERVAL = 4000;
	const ANIMATION_DURATION = 850;
	const fadeEase = [0.22, 1, 0.36, 1] as const;
	// All cards share one DOM size — the only differences across roles are
	// transform values (x, scale, opacity, blur). A single duration + ease keeps
	// every property locked together, so nothing can outrun anything else and
	// produce the "big-then-small" jolt during a role flip.
	const cardTransition = {
		duration: 0.85,
		ease: fadeEase,
	};
	// Percent of card width — sized so the side cards tuck slightly behind
	// the center card (a "little hiding" peek) instead of sitting fully clear.
	const sideOffset = 68;
	const sidePeekY = 18;
	const enterExitOffset = 130;

	const nextSlide = useCallback(() => {
		if (isAnimating) return;
		setIsAnimating(true);
		setDirection(1);
		setCurrentIndex((prev) => (prev + 1) % projects.length);
		setTimeout(() => setIsAnimating(false), ANIMATION_DURATION);
	}, [isAnimating]);

	const buttonVariants = {
		initial: { scale: 1 },
		hover: {
			scale: 1,
			transition: {
				type: "spring" as const,
				stiffness: 340,
				damping: 18,
			},
		},
		tap: { scale: 0.9 },
	};

	const prevSlide = useCallback(() => {
		if (isAnimating) return;
		setIsAnimating(true);
		setDirection(-1);
		setCurrentIndex((prev) => (prev - 1 + projects.length) % projects.length);
		setTimeout(() => setIsAnimating(false), ANIMATION_DURATION);
	}, [isAnimating]);

	// Auto-play functionality
	useEffect(() => {
		if (isAutoPlaying && !isAnimating) {
			autoPlayRef.current = setInterval(() => {
				nextSlide();
			}, AUTO_PLAY_INTERVAL);
		}
		return () => {
			if (autoPlayRef.current) {
				clearInterval(autoPlayRef.current);
				autoPlayRef.current = null;
			}
		};
	}, [isAutoPlaying, isAnimating, nextSlide]);

	// Pause auto-play on hover
	const handleMouseEnter = () => {
		setIsAutoPlaying(true);
	};

	const handleMouseLeave = () => {
		setIsAutoPlaying(true);
	};

	const handleTouchStart = (e: React.TouchEvent) => {
		touchStartX.current = e.touches[0].clientX;
		setIsAutoPlaying(true);
	};

	const handleTouchEnd = (e: React.TouchEvent) => {
		touchEndX.current = e.changedTouches[0].clientX;
		const diff = touchStartX.current - touchEndX.current;
		if (Math.abs(diff) > 50) {
			if (diff > 0) nextSlide();
			else prevSlide();
		}
		setTimeout(() => setIsAutoPlaying(true), 3000);
	};

	const openModal = (project: ProjectData) => {
		setSelectedProject(project);
		setIsExpanded(true);
		setIsAutoPlaying(false);
	};

	const closeModal = () => {
		setIsExpanded(false);
		setSelectedProject(null);
		setIsAutoPlaying(true);
	};

	const currentProject = projects[currentIndex];
	const prevProject =
		projects[(currentIndex - 1 + projects.length) % projects.length];
	const nextProject = projects[(currentIndex + 1) % projects.length];
	const visibleProjects =
		showSideCards ?
			[
				{ project: prevProject, role: "prev" as const },
				{ project: currentProject, role: "current" as const },
				{ project: nextProject, role: "next" as const },
			]
		:	[{ project: currentProject, role: "current" as const }];

	const cardPositions = {
		prev: {
			x: `${-sideOffset}%`,
			y: sidePeekY,
			scale: 0.8,
			opacity: 0.68,
			filter: "blur(0.8px)",
			zIndex: 10,
		},
		current: {
			x: "0%",
			y: 0,
			scale: 1,
			opacity: 1,
			filter: "blur(0px)",
			zIndex: 20,
		},
		next: {
			x: `${sideOffset}%`,
			y: sidePeekY,
			scale: 0.8,
			opacity: 0.68,
			filter: "blur(0.8px)",
			zIndex: 10,
		},
	};

	const getCardInitialState = (role: "prev" | "current" | "next") => {
		if (role === "current") {
			return {
				x: `${direction > 0 ? sideOffset : -sideOffset}%`,
				y: 0,
				scale: 0.82,
				opacity: 0,
				filter: "blur(3px)",
			};
		}

		return {
			x: `${role === "prev" ? -enterExitOffset : enterExitOffset}%`,
			y: 0,
			scale: 0.78,
			opacity: 0,
			filter: "blur(3px)",
		};
	};

	const getCardExitState = (role: "prev" | "current" | "next") => {
		if (role === "current") {
			return {
				x: `${direction > 0 ? -sideOffset : sideOffset}%`,
				y: 0,
				scale: 0.82,
				opacity: 0,
				filter: "blur(3px)",
			};
		}

		return {
			x: `${role === "prev" ? -enterExitOffset : enterExitOffset}%`,
			y: 0,
			scale: 0.78,
			opacity: 0,
			filter: "blur(3px)",
		};
	};

	const data = {
		hero: {
			title: t("worldwideSlider.title"),
			description: t("worldwideSlider.description"),
		},
	};

	return (
		<>
			<section
				ref={sectionRef}
				className="relative w-full overflow-hidden bg-[#3b0092] px-4 py-14 sm:px-6 lg:px-8 lg:py-20 "
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}>
				<div className="absolute inset-0">
					<DotGrid
						dotSize={4}
						gap={15}
						baseColor="#2c016c"
						activeColor="#f09a38"
						proximity={100}
						shockRadius={200}
						shockStrength={3}
						resistance={750}
						returnDuration={1.5}
					/>
				</div>{" "}
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
					className="relative z-10 mx-auto mb-8 max-w-5xl text-center lg:mb-10">
					<motion.h2
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className="mb-4  from-white via-white to-white/70 bg-clip-text text-3xl font-semibold tracking-tight text-white sm:text-4xl md:text-5xl lg:mb-6 py-2 lg:text-6xl font-display">
						{data.hero.title}
					</motion.h2>

					<motion.p
						initial={{ opacity: 0, y: 10 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.6, delay: 0.3 }}
						className="mx-auto max-w-3xl text-sm leading-relaxed text-gray-300 sm:text-base md:text-lg lg:text-xl">
						{data.hero.description}
					</motion.p>
				</motion.div>
				{/* Main Container */}
				<div
					className="relative z-10 flex items-center justify-center py-6 sm:py-8"
					onTouchStart={handleTouchStart}
					onTouchEnd={handleTouchEnd}>
					<div className="mx-auto w-full max-w-7xl">
						{/* Carousel */}
						<div className="relative overflow-visible">
							<div className="relative flex h-105 items-center justify-center px-4 sm:h-115 md:h-125 lg:px-16 xl:px-20">
								<div className="pointer-events-none absolute inset-x-10 top-6 hidden h-[86%] rounded-[2.25rem] bg-linear-to-br from-[#f09a38]/10 via-white/0 to-[#7c3aed]/15 blur-3xl lg:block" />
								<div className="relative z-20 mx-auto flex h-105 w-full max-w-5xl items-center justify-center px-2 sm:h-115 sm:px-4 md:h-125">
									<div
										className="relative h-full w-full max-w-lg lg:max-w-4xl"
										style={{ perspective: "1400px" }}>
										<AnimatePresence
											initial={false}
											mode="popLayout">
											{visibleProjects.map(({ project, role }) => (
												<motion.div
													key={project.id}
													className={`absolute inset-0 ${
														role === "current" ? "" : "cursor-pointer"
													}`}
													style={{
														willChange: "transform, opacity, filter",
														transformOrigin: "center center",
													}}
													initial={getCardInitialState(role)}
													animate={cardPositions[role]}
													exit={getCardExitState(role)}
													transition={cardTransition}
													onClick={
														role === "prev" ? prevSlide
														: role === "next" ?
															nextSlide
														:	undefined
													}
													whileHover={
														role === "prev" ?
															{
																x: `${-(sideOffset + 8)}%`,
																y: sidePeekY - 10,
																scale: 0.88,
																opacity: 0.92,
																filter: "blur(0px)",
															}
														: role === "next" ?
															{
																x: `${sideOffset + 8}%`,
																y: sidePeekY - 10,
																scale: 0.88,
																opacity: 0.92,
																filter: "blur(0px)",
															}
														:	undefined
													}>
													<ProjectCard
														project={project}
														isActive={role === "current"}
														onExpand={
															role === "current" ?
																() => openModal(project)
															:	undefined
														}
													/>
												</motion.div>
											))}
										</AnimatePresence>
									</div>
								</div>
							</div>
						</div>

						{/* Slide Indicators with animation */}
						<div className="mt-6 flex justify-center gap-2">
							{projects.map((_, idx) => (
								<motion.button
									key={idx}
									onClick={() => {
										if (!isAnimating && idx !== currentIndex) {
											const diff = idx - currentIndex;
											const steps = diff > 0 ? diff : diff + projects.length;
											setDirection(steps === 1 ? 1 : -1);
											setIsAnimating(true);
											setCurrentIndex(idx);
											setTimeout(
												() => setIsAnimating(false),
												ANIMATION_DURATION,
											);
										}
									}}
									aria-label={`Go to slide ${idx + 1}`}
									className={`h-1.5 rounded-full transition-all duration-300 ${
										idx === currentIndex ?
											"w-8 bg-linear-to-r from-[#f09a38] to-[#f7c977] shadow-[0_0_12px_rgba(240,154,56,0.55)]"
										:	"w-1.5 bg-white/30 hover:bg-white/60"
									}`}
									whileHover={{ scale: 1.1 }}
									whileTap={{ scale: 0.9 }}
									transition={{ duration: 0.28, ease: fadeEase }}
								/>
							))}
						</div>

						{/* CTA Button */}
						<Link to={"/worldwide-projects"}>
							<motion.div
								initial={{ opacity: 0, y: 50 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{ once: true }}
								transition={{ duration: 0.9, delay: 0.4, ease: fadeEase }}
								className="px-4 py-5 text-center sm:py-6">
								<motion.button
									variants={buttonVariants}
									className="premium-button-bg-white group w-full sm:w-auto px-6 sm:px-8 md:px-10 mt-2 py-3 sm:py-4 text-sm sm:text-base">
									{t("worldwideSlider.cta")}
									<svg
										className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M14 5l7 7m0 0l-7 7m7-7H3"
										/>
									</svg>
								</motion.button>
							</motion.div>
						</Link>
					</div>
				</div>
			</section>
		</>
	);
};

// Enhanced Project Card Component
interface ProjectCardProps {
	project: ProjectData;
	isActive?: boolean;
	isSmall?: boolean;
	onClick?: () => void;
	onExpand?: () => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
	project,
	isActive,
	isSmall,
	onClick,
}) => {
	const { t } = useTranslation();
	const { title, appKey, image, country: countryKey, profile, tagKeys = [] } =
		project;

	// All display text resolves from the active locale.
	const application = appKey ? t(`worldwideSlider.projects.${appKey}`) : undefined;
	const country = countryKey ? t(`worldwideSlider.countries.${countryKey}`) : undefined;
	const tags = tagKeys.map((k) => t(`worldwideSlider.tags.${k}`));

	// Build a comparable, localized spec table per project profile. Technical
	// codes / material grades stay literal; descriptive values are translated.
	const L = (k: string) => t(`worldwideSlider.specLabels.${k}`);
	const V = (k: string) => t(`worldwideSlider.specValues.${k}`);
	let designStandard = V("asmeApiCompliant");
	let construction = V("customEngineered");
	let tubesheet = V("carbonSteel");
	if (profile === "airCooler") {
		designStandard = "API 661";
		construction = V("plateFinnedForcedDraft");
		tubesheet = V("carbonSteel");
	} else if (profile === "heatExchanger") {
		designStandard = "ASME Sec VIII Div.1";
		construction = V("temaShellTube");
		tubesheet = "SA 516 Gr. 70";
	} else if (profile === "water") {
		designStandard = "ASME B31.3";
		construction = V("doublePipeHeatExchanger");
		tubesheet = "SS 316";
	}
	const specs: [string, string][] = [
		[L("designStandard"), designStandard],
		[L("construction"), construction],
		[L("tubesheet"), tubesheet],
		[L("inspection"), V("thirdPartyWitnessed")],
		[L("fabrication"), V("customEngineered")],
	];

	return (
		<motion.div
			onClick={onClick}
			whileHover={{ y: isActive ? -6 : -4 }}
			transition={{ type: "spring", stiffness: 280, damping: 22 }}
			className={`group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 transition-all duration-500 ease-out ${
				isSmall ? "h-90" : "h-105 sm:h-115 md:h-125"
			} ${
				isActive ?
					"cursor-default shadow-[0_30px_80px_-15px_rgba(0,0,0,0.7)] ring-1 ring-[#f09a38]/40"
				:	"cursor-pointer shadow-xl shadow-black/40 ring-1 ring-white/10"
			}`}>
			{/* FULL-BLEED IMAGE BACKGROUND */}
			{image ?
				<img
					src={image}
					alt={title}
					loading="lazy"
					decoding="async"
					className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
				/>
			:	<div className="absolute inset-0 flex items-center justify-center bg-slate-800">
					<Factory
						aria-hidden
						className="h-16 w-16 text-slate-500"
					/>
				</div>
			}

			{/* Scrim — concentrated at bottom so the image stays visible above */}
			<div
				aria-hidden
				className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/45 via-black/15 to-transparent"
			/>
			<div
				aria-hidden
				className="absolute inset-x-0 bottom-0 h-[78%] bg-gradient-to-t from-[#0b0327] via-[#0b0327]/85 via-30% to-transparent"
			/>

			{/* FOREGROUND — fills card via flex-1 so content can use mt-auto reliably */}
			<div
				className={`relative z-10 flex flex-1 flex-col ${
					isSmall ? "p-3.5" : "p-4 sm:p-5"
				}`}>
			

				{/* Bottom stack — pinned to the bottom edge */}
				<motion.div
					initial={false}
					animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0.95, y: 0 }}
					transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
					className="mt-auto flex flex-col gap-2.5 pt-6">
					{country && (
						<span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-white shadow-md ring-1 ring-white/20 backdrop-blur">
							<Globe
								aria-hidden
								className="h-3 w-3 text-[#f09a38]"
							/>
							{country}
						</span>
					)}

					<header className="space-y-1">
						<h3
							className={`font-semibold leading-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] ${
								isSmall ? "text-sm" : "text-lg sm:text-xl md:text-2xl"
							}`}>
							{title}
						</h3>
						{application && (
							<p
								className={`leading-relaxed text-slate-200/95 drop-shadow ${
									isSmall ? "text-[11px]" : "text-[12.5px] sm:text-[13.5px]"
								}`}>
								{application}
							</p>
						)}
					</header>

					{tags.length > 0 && (
						<ul
							className="flex flex-wrap gap-1.5"
							aria-label={t("common.ui.applications")}>
							{tags.map((tag) => (
								<li
									key={tag}
									className="rounded-md border border-[#f09a38]/40 bg-[#f09a38]/15 px-2 py-0.5 text-[11px] font-medium text-[#f9c178] backdrop-blur">
									{tag}
								</li>
							))}
						</ul>
					)}

					{specs.length > 0 && (
						<div className="overflow-hidden rounded-xl border border-white/15 bg-black/5 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.4)] transition-all duration-500 group-hover:border-[#f09a38]/40 group-hover:bg-black/10">
							<div className="border-b border-white/12 px-3.5 py-2">
								<h4 className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/85 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">
									{t("worldwideSlider.specifications")}
								</h4>
							</div>
							<dl
								className={`grid gap-px bg-white/10 ${
									isSmall ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-2"
								}`}>
								{specs.map(([key, value]) => (
									<div
										key={key}
										className="flex flex-col gap-0.5 bg-black/8 px-3 py-2 transition-colors duration-300 hover:bg-black/18">
										<dt className="text-[9.5px] font-medium uppercase tracking-[0.12em] text-white/70 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">
											{key}
										</dt>
										<dd className="break-words text-[12px] font-semibold leading-snug text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.85)]">
											{value}
										</dd>
									</div>
								))}
							</dl>
						</div>
					)}
				</motion.div>
			</div>
		</motion.div>
	);
};

export default WorldWideProjects;
